import type { ReactNode } from "react";

import {
    createToastStore,
    type ToastId,
    type ToastOptions,
    type ToastRenderContext,
    type ToastVariant,
} from "./toastStore";

/** Глобальный стор: один на вкладку, его показывает `ToastWrapper` из `src/app/providers.tsx`. */
export const toastStore = createToastStore();

export interface ToastMessageOptions extends ToastOptions {
    title?: ReactNode;
}

const isBrowser = typeof window !== "undefined";

function show(variant: ToastVariant, message: ReactNode, { title, ...options }: ToastMessageOptions = {}): ToastId {
    // Вне браузера (тест в окружении node) показывать нечего.
    if (!isBrowser) return options.id ?? "";
    return toastStore.upsert({ type: "message", variant, title, message }, options);
}

/**
 * Уведомления в правом нижнем углу. Вызываются из любого клиентского кода:
 * обработчиков, эффектов, колбэков React Query, обычных функций.
 *
 * @example
 * toast.success("Карточка сохранена");
 * toast.error("Не удалось сохранить", { duration: null }); // висит, пока не закроют
 *
 * const id = toast.info("Скачивание файла…", { duration: null });
 * await download();
 * toast.success("Файл скачан", { id }); // тот же элемент меняет текст и снова закроется через 5 с
 * // или просто закрыть: toast.dismiss(id);
 *
 * toast.custom(({ dismiss }) => <ToastUpload file={file} onClose={dismiss} />, { duration: null });
 */
export const toast = {
    show,
    success: (message: ReactNode, options?: ToastMessageOptions) => show("success", message, options),
    error: (message: ReactNode, options?: ToastMessageOptions) => show("danger", message, options),
    warning: (message: ReactNode, options?: ToastMessageOptions) => show("warning", message, options),
    info: (message: ReactNode, options?: ToastMessageOptions) => show("info", message, options),

    /** Произвольный компонент. Для своих toast-ов используйте `ToastSurface` и `ToastCloseButton`. */
    custom(render: (context: ToastRenderContext) => ReactNode, options?: ToastOptions): ToastId {
        if (!isBrowser) return options?.id ?? "";
        return toastStore.upsert({ type: "custom", render }, options);
    },

    /** Закрыть элемент по id (например, когда завершилась загрузка); без id — закрыть все. */
    dismiss(id?: ToastId): void {
        toastStore.dismiss(id);
    },

    isActive(id: ToastId): boolean {
        return toastStore.isActive(id);
    },
};
